import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Inbox as InboxIcon, Flag, Users, Repeat, CheckSquare,
  MessageSquare, BarChart3, Zap, Settings, UserCircle, Bell, Search,
  Moon, Sun, LogOut, PanelLeftClose, PanelLeftOpen, LifeBuoy,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api, timeAgo } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { CreditsWidget, CreditsExhaustedDialog } from "../components/CreditsWidget";

const NAV = [
  { to: "/app", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/inbox", key: "inbox", icon: InboxIcon },
  { to: "/app/priorites", key: "priorities", icon: Flag },
  { to: "/app/prospects", key: "prospects", icon: Users },
  { to: "/app/relances", key: "followups", icon: Repeat },
  { to: "/app/taches", key: "tasks", icon: CheckSquare },
  { to: "/app/ask", key: "ask", icon: MessageSquare },
  { to: "/app/analytics", key: "analytics", icon: BarChart3 },
];

const NAV_BOTTOM = [
  { to: "/app/automatisations", key: "automations", icon: Zap },
  { to: "/app/parametres", key: "settings", icon: Settings },
  { to: "/app/compte", key: "account", icon: UserCircle },
  { to: "/contact", key: "contact", icon: LifeBuoy },
];

function NotificationsBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t2 = setInterval(load, 30000); return () => clearInterval(t2); }, []);
  return (
    <div className="relative">
      <button onClick={() => { setOpen(!open); if (!open) load(); }} data-testid="notifications-button"
        className="relative p-2 rounded-full hover:bg-secondary transition-colors">
        <Bell size={17} />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="notifications-badge">
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 w-80 rounded-2xl border border-border bg-popover shadow-xl z-50 overflow-hidden" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <p className="text-xs font-bold">{t("nav.notifications")}</p>
            <button onClick={async () => { await api.post("/notifications/read-all"); load(); }} data-testid="notifications-read-all"
              className="text-[11px] text-electric hover:underline">{t("nav.markAllRead")}</button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.notifications.length === 0 && (
              <p className="px-4 py-8 text-xs text-muted-foreground text-center">{t("nav.noNotifications")}</p>
            )}
            {data.notifications.map((n) => (
              <div key={n.notification_id} className={`px-4 py-3 border-b border-border/50 text-xs leading-relaxed ${n.read ? "text-muted-foreground" : ""}`}>
                {!n.read && <span className="inline-block h-1.5 w-1.5 rounded-full bg-electric mr-2 align-middle" />}
                {n.text}
                <span className="block text-[10px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [q, setQ] = useState("");

  const onSearch = (e) => {
    if (e.key === "Enter" && q.trim()) navigate(`/app/inbox?q=${encodeURIComponent(q.trim())}`);
  };

  const itemClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-colors ${
      isActive ? "bg-electric/10 text-electric" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`;

  return (
    <div className="min-h-screen flex bg-background" data-testid="app-layout">
      <aside className={`${collapsed ? "w-[68px]" : "w-60"} shrink-0 border-r border-border bg-card/60 backdrop-blur-xl flex flex-col h-screen sticky top-0 transition-[width] duration-300`} data-testid="sidebar">
        <div className={`flex items-center gap-2 px-4 h-16 border-b border-border/60 ${collapsed ? "justify-center" : ""}`}>
          <Link to="/" className="flex items-center gap-2" data-testid="sidebar-logo">
            <div className="h-8 w-8 rounded-lg bg-primary dark:bg-electric flex items-center justify-center shrink-0">
              <InboxIcon size={16} className="text-white" />
            </div>
            {!collapsed && <span className="font-display font-bold tracking-tight">{t("common.appName")}</span>}
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={itemClass} data-testid={`nav-${n.key}`}>
              <n.icon size={17} className="shrink-0" />
              {!collapsed && t(`nav.${n.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 pb-2 space-y-1">
          {NAV_BOTTOM.map((n) => (
            <NavLink key={n.to} to={n.to} className={itemClass} data-testid={`nav-${n.key}`}>
              <n.icon size={17} className="shrink-0" />
              {!collapsed && t(`nav.${n.key}`)}
            </NavLink>
          ))}
        </div>
        <div className={`border-t border-border/60 p-3 ${collapsed ? "flex justify-center" : ""}`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <CreditsWidget collapsed />
              <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button"
              title={t("nav.logout")}
              className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
              <LogOut size={16} />
            </button>
            </div>
          ) : (
            <div className="space-y-2.5">
              <CreditsWidget />
              <div className="flex items-center gap-3 px-1">
                <div className="h-8 w-8 rounded-full bg-electric/15 text-electric flex items-center justify-center text-xs font-bold shrink-0">
                  {(user?.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate" data-testid="sidebar-user-email">{user?.email}</p>
                  {user?.workspace_name && (
                    <p className="text-[10px] text-electric truncate" data-testid="sidebar-workspace-name">{user.workspace_name}</p>
                  )}
                </div>
              </div>
              <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button"
                className="w-full rounded-xl border border-destructive/30 text-destructive text-xs font-bold px-3 py-2 hover:bg-destructive/10 transition-colors flex items-center justify-center gap-1.5">
                <LogOut size={13} /> {t("nav.logout")}
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border/60 glass sticky top-0 z-40 flex items-center gap-3 px-5">
          <button onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-toggle"
            className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground">
            {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
          </button>
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearch}
              placeholder={t("nav.searchPlaceholder")} data-testid="global-search"
              className="w-full rounded-full border border-border bg-card pl-10 pr-4 py-2 text-xs outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow" />
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <LanguageSwitcher />
            <NotificationsBell />
            <button onClick={toggle} data-testid="app-theme-toggle" className="p-2 rounded-full hover:bg-secondary transition-colors">
              {dark ? <Sun size={17} /> : <Moon size={17} />}
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
      <CreditsExhaustedDialog />
    </div>
  );
}
